/**
 * Return the first sentence of `text`, treating `.`, `?`, `!` as terminators.
 * Common abbreviations like `e.g.` and `i.e.` are not counted as terminators.
 * Returns "" for empty / whitespace-only input.
 */
export function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  // Use a non-printable sentinel so we don't collide with real spaces or
  // punctuation in the input when we restore the masked periods later.
  const SENTINEL = "";
  const masked = trimmed
    .replaceAll("e.g.", `e${SENTINEL}g${SENTINEL}`)
    .replaceAll("i.e.", `i${SENTINEL}e${SENTINEL}`)
    .replaceAll("etc.", `etc${SENTINEL}`);

  const match = masked.match(/^[^.?!]*[.?!]/);
  if (!match) return trimmed;
  return match[0].replaceAll(SENTINEL, ".");
}

/**
 * Clip `text` to at most `max` characters. If the input is longer, trim back
 * to the last word boundary and append an ellipsis. Total length never
 * exceeds `max`.
 */
export function clipSentence(text: string, max: number): string {
  if (text.length <= max) return text;
  const budget = max - 1; // reserve 1 char for "…"
  const slice = text.slice(0, budget);
  const lastSpace = slice.lastIndexOf(" ");
  const trimmed = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  return `${trimmed.trimEnd()}…`;
}
